import { Component, Input, OnInit, OnDestroy, OnChanges, inject, ViewChild, AfterViewInit, effect, EffectRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/shared/material.module';
import { CardsService, DigitalCard } from 'src/app/services/cards.service';
import { SharedTableComponent } from '@tamilarasitech/shared-ui-components';
import { MembersTableService } from 'src/app/services/members-table.service';
import { MembersService, Member } from 'src/app/services/members.service';
import { Subscription } from 'rxjs';
import { BreadcrumbComponent } from 'src/app/components/shared/breadcrumb/breadcrumb.component';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-members',
  standalone: true,
  imports: [CommonModule, MaterialModule, SharedTableComponent, BreadcrumbComponent],
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.scss']
})
export class MembersComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() card: DigitalCard | null = null;
  @ViewChild('sharedTable') sharedTable: any;

  private membersTableService = inject(MembersTableService);
  private membersService = inject(MembersService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private subscriptions: Subscription[] = [];
  // store effect reference for cards signal
  private cardsEffectRef: EffectRef | null = null;

  members: Member[] = [];
  loading = false;
  error: string | null = null;
  importErrors: string[] = [];
  currentUser: any = null;
  showLicensePopup = false;
  // optional: message shown when blocked
  licenseBlockMessage = 'License limit reached. Please upgrade to add more members.';
  
    constructor(private cardsService: CardsService) {}
    getCachedCards() {
    return this.cardsService.cards();  // returns cached DigitalCard[]
  }
  ngOnInit() {
      const sub = this.authService.user$.subscribe((user: any) => {
      this.currentUser = user;
      if (user) {
        console.log('Current user loaded for license check:', user);
        // Ensure cards are loaded for initial license calculation
        this.cardsService.getUserCards().then(cards => {
          const totalLicenses = this.calculateUsedLicenses();
          console.log(`Licenses used: ${totalLicenses} of ${user.license}`);
        }).catch(err => {
          // If fetching fails, still attempt calculation (may be empty)
          console.warn('Failed to fetch cards for license check:', err);
          const totalLicenses = this.calculateUsedLicenses();
          console.log(`Licenses used (fallback): ${totalLicenses} of ${user.license}`);
        });
      }
    });
    this.subscriptions.push(sub);
    // react to changes to the cards signal so license calc updates automatically
    this.cardsEffectRef = effect(() => {
      const cards = this.getCachedCards();
      // access to ensure the effect depends on the cards signal
      // trigger recalculation when cards change and a user is present
      if (this.currentUser) {
        const used = this.calculateUsedLicenses();
        console.log('🔁 [License Check] Recalculated used licenses after cards change:', used);
      }
      return cards;
    });
    // call once to eagerly read cached cards (no-op if empty)
    this.getCachedCards();
    this .isLicenseLimitReached; // Initial calculation
    this.setupSubscriptions();
    this.loadMembers();
}
    // Calculate total used licenses across all cards
    calculateUsedLicenses(): number {
      const cards = this.getCachedCards();
      console.log('🔍 [License Check] Calculating used licenses from cards:', cards);
      let totalLicensesUsed = 0;
      cards.forEach(card => {
      totalLicensesUsed += 1; // 1 license per card itself
      if (card.employeescount) {
      totalLicensesUsed += card.employeescount;
      }
      });
      console.log('🔍 [License Check] Total licenses used across all cards:', totalLicensesUsed);
      return totalLicensesUsed;
      }
  ngOnChanges() {
    // Reload members when card changes
    this.loadMembers();
    // The table will handle reloading through its normal lifecycle
    console.log('[MembersComponent] Card changed, table will reload automatically');
  }

  ngAfterViewInit() {
    // Let the table handle its own initial loading
    // The table will call onLoadPage automatically when it initializes
    console.log('[MembersComponent] View initialized, table will auto-load');
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    // dispose the cards effect if created
    if (this.cardsEffectRef) {
      try {
        this.cardsEffectRef.destroy();
      } catch (e) {
        // ignore disposal errors
      }
      this.cardsEffectRef = null;
    }
  }

  private triggerTableInitialLoad() {
    if (this.card?.id && this.sharedTable) {
      console.log('[MembersComponent] Triggering initial table load');
      this.onLoadPage({ cardId: this.card.id }, this.sharedTable);
    }
  }

  private updateTableData(members: Member[]) {
    if (this.sharedTable) {
      console.log('[MembersComponent] Attempting to update table data directly:', members);
      
      // Try different ways to set data on the table
      if (this.sharedTable.data !== undefined) {
        this.sharedTable.data = members;
        console.log('[MembersComponent] Set data on table.data');
      }
      
      if (this.sharedTable.pagedData !== undefined) {
        this.sharedTable.pagedData = members;
        console.log('[MembersComponent] Set data on table.pagedData');
      }
      
      if (this.sharedTable.totalRecords !== undefined) {
        this.sharedTable.totalRecords = members.length;
        console.log('[MembersComponent] Set totalRecords:', members.length);
      }
      
      // Force change detection
      if (this.sharedTable.cdr && this.sharedTable.cdr.detectChanges) {
        this.sharedTable.cdr.detectChanges();
        console.log('[MembersComponent] Triggered change detection');
      }
    }
  }

  private setupSubscriptions() {
    // Subscribe to members data
    this.subscriptions.push(
      this.membersService.members$.subscribe(members => {
        this.members = members;
        console.log('[MembersComponent] Subscription received members:', members);
        
        // Sync cache with service data when members change
        if (this.card?.id) {
          if (members.length > 0) {
            this.membersTableService.refreshCache(this.card.id);
          }
          // Always try to update table data, even if empty
          setTimeout(() => {
            this.updateTableData(members);
          }, 2000); // Give table time to initialize
        }
      })
    );

    // Subscribe to loading state
    this.subscriptions.push(
      this.membersService.loading$.subscribe(loading => {
        this.loading = loading;
      })
    );

    // Subscribe to error state
    this.subscriptions.push(
      this.membersService.error$.subscribe(error => {
        this.error = error;
      })
    );
  }

  private loadMembers() {
    // Data loading is now handled by the table's onLoadPage callback
    // This method can be used for any additional initialization if needed
    console.log('[MembersComponent] Members component initialized for card:', this.card?.id);
  }

  // Table event handlers
  onLoadPage = async (event: any, tableRef: any) => {
  try {
    const result = await this.membersTableService.onLoadPage(
      { ...event, cardId: this.card?.id }, 
      tableRef
    );
    console.log('[MembersComponent] Load page result:', result);
    
    if (result) {
      const pageSize = event.pageSize || tableRef.pageSize || 10;
      const currentPage = tableRef.currentPage || 1;
      
      // ✅ Calculate pagination slice
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      // ✅ Get only the current page's data
      const pagedData = result.data.slice(startIndex, endIndex);
      
      tableRef.data = pagedData;
      tableRef.totalRecords = result.total || 0;
      tableRef.hasMore = endIndex < result.total;  // ✅ Calculate hasMore
      tableRef.lastDoc = result.lastDoc || null;
      tableRef.updateDisplayIndices();
      
      console.log('[MembersComponent] Table synced:', {
        page: currentPage,
        pageSize: pageSize,
        displayingItems: pagedData.length,
        totalRecords: tableRef.totalRecords,
        hasMore: tableRef.hasMore
      });
    }
    
    return result;
  } catch (error) {
    console.error('Error loading page:', error);
    return { data: [], total: 0, hasMore: false, loading: false };
  }
};

  onGetTotal = async (event: any, tableRef: any) => {
  try {
    const total = await this.membersTableService.onGetTotal(
      { ...event, cardId: this.card?.id }, 
      tableRef
    );
    
    console.log('[MembersComponent] Total received:', total);
    
    // ✅ Set totalRecords immediately
    tableRef.totalRecords = total;
    tableRef.updateDisplayIndices();
    
    return total;
  } catch (error) {
    console.error('Error getting total:', error);
    return 0;
  }
};

   onAdd = async (event: any) => {
    try {
      // License check: 1 member = 1 license
      if (this.card?.id) {
        if (this.isLicenseLimitReached) {
          console.warn('[MembersComponent] Create member blocked - license limit reached');
          // Optionally navigate to upgrade page or show a popup
          // this.showLicensePopup = true;
          throw new Error(this.licenseBlockMessage);
        }
      }
      const result = await this.membersTableService.onAdd({ ...event, cardId: this.card?.id });
      console.log('Member added successfully:', result);
      return result;
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    }
  };

  onUpdate = async (event: any) => {
    try {
      const result = await this.membersTableService.onUpdate({ ...event, cardId: this.card?.id });
      console.log('Member updated successfully:', result);
      return result;
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  };

  onDelete = async (event: any) => {
    try {
      const result = await this.membersTableService.onDelete({ ...event, cardId: this.card?.id });
      console.log('Member deleted successfully:', result);
      return result;
    } catch (error) {
      console.error('Error deleting member:', error);
      throw error;
    }
  };

  onDeleteBatch = async (event: any) => {
    try {
      const result = await this.membersTableService.onDeleteBatch({ ...event, cardId: this.card?.id });
      console.log('Members batch deleted successfully:', result);
      return result;
    } catch (error) {
      console.error('Error deleting members batch:', error);
      throw error;
    }
  };

  onDeleteAll = async () => {
    try {
      if (!this.card?.id) {
        throw new Error('Card ID is required');
      }
      await this.membersTableService.onDeleteAll(this.card.id);
      console.log('All members deleted successfully');
    } catch (error) {
      console.error('Error deleting all members:', error);
      throw error;
    }
  };

  onUploadFile = async (event: any) => {
    try {
      await this.membersTableService.onUploadFile({ ...event, cardId: this.card?.id });
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  onImportData = async (event: any) => {
    try {
      const response = await this.membersTableService.onImportData({ ...event, cardId: this.card?.id });
      console.log('Members imported successfully', response);

      // Handle and display error messages for missing fields
      this.importErrors = [];
      if (response.errors && response.errors.length > 0) {
        this.importErrors = response.errors.map((err: any) => {
          const missingFields = [];
          if (!err.data.handler) missingFields.push('handler');
          if (!err.data.contact_info?.contactName) missingFields.push('name');
          const displayName = err.data.contact_info?.contactName || `Row ${err.index + 1}`;
          return `${displayName}: Missing ${missingFields.join(' and ')}`;
        });
        // Optionally log errors
        console.error('Import errors:', this.importErrors);
      }
    } catch (error) {
      console.error('Error importing members:', error);
      throw error;
    }
  };
// Add this near your other methods
onLicenseLimitReached() {
  this.showLicensePopup = true;
  // Auto-hide after 4 seconds
  // setTimeout(() => {
  //   this.showLicensePopup = false;
  // }, 4000);
}
  // Export members to CSV
  exportMembers() {
    this.membersTableService.exportMembersToCSV();
  }
  importMembers() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          const csvText = e.target.result;
          const members = this.parseCSV(csvText);
          // Pass parsed members to the table import handler
          this.onImportData({ cardId: this.card?.id, items: members });
        };
        reader.readAsText(file);
      }
    };
    fileInput.click();
  }

  clearImportErrors() {
    this.importErrors = [];
  }

  // Simple CSV parser (assumes header row and comma separation)
parseCSV(csv: string): any[] {
  const lines = csv.trim().split('\n'); 
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const values = line.split(',').map(v => v.replace(/^"|"$/g, '')); 
    const obj: any = {};
    headers.forEach((h, i) => obj[h] = values[i]);
    return obj;
  });
}

  goToUpgradeLicense() {
    this.router.navigate(['/upgrade-license']);
  }


  get isLicenseLimitReached(): boolean {
const licenseValue = parseInt(this.currentUser?.license || '0', 10);
if (!licenseValue) return false;
const used = this.calculateUsedLicenses();
console.log('🔍 [License Check] Used licenses:', used, 'of', licenseValue);
return used >= licenseValue;
}
}